import { ArrowDownTrayIcon, DocumentCheckIcon, EyeIcon } from "@heroicons/react/24/outline";
import { format } from "date-fns";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { Badge, BadgeProps, Column, Paragraph, Table, Tooltip } from "@fronterahealth/frontera-ui-components";

import ManageVideoConsentFormDialog from "@components/ManageVideoConsentFormDialog/ManageVideoConsentFormDialog";
import { useAdminData } from "@providers/AdminDataProvider";

export const INTAKE_VALUES = ["Complete", "In Progress", "Started", "Awaiting"] as const;
type IntakeType = (typeof INTAKE_VALUES)[number];
export const STAGE_VALUES = [
  "Diagnosis Waitlist",
  "Diagnosis Pending",
  "Assessment Waitlist",
  "Assessment Pending",
  "Assessment Complete",
] as const;

const BadgeSettingFromIntake: { [key in IntakeType]: BadgeProps["appearance"] } = {
  Complete: "success",
  Awaiting: "warn",
  "In Progress": "neutral",
  Started: "neutral",
};

export type StageType = (typeof STAGE_VALUES)[number];

export interface FronteraClient {
  id: string;
  clientId: string;
  name: string;
  caregiver: string;
  intake?: IntakeType;
  intakePagesCompleted: number;
  created: Date;
  nextAppointment?: Date;
  stage: StageType;
  nearestLocation?: string;
  dob: string;
  hasCaregiverConsent: boolean;
  caregivers: { id: string; name: string; consentGiven: boolean }[];
  archived?: boolean;
}

interface ClientTableProps {
  clients: (FronteraClient | null)[];
  loading?: boolean;
  onRowClick: (client: FronteraClient) => void;
  onImport?: () => void;
  emptyMessage?: React.ReactNode;
}

export const ClientTable: React.FC<ClientTableProps> = ({ clients, loading, onRowClick, onImport, emptyMessage }) => {
  const go = useNavigate();
  const { isOnboardingEnabled, isConsentRequired } = useAdminData();
  const [managingConsentForClient, setManagingConsentForClient] = useState<FronteraClient>();
  const columns: Column<FronteraClient>[] = [
    {
      dataIndex: "name",
      key: "name",
      title: "Name",
      render: (value: string, record: FronteraClient) => (
        <div className="flex items-center gap-2 ph-no-capture">
          <span>{value}</span>
          {record.archived && <Badge appearance="neutral" text="Archived" />}
        </div>
      ),
    },
    {
      dataIndex: "dob",
      key: "dob",
      title: "Date of Birth",
      render: (value: string) => <span className="ph-no-capture">{value}</span>,
    },
    {
      dataIndex: "caregiver",
      key: "caregiver",
      title: "Caregiver",
      render: (value: string) => <span className="ph-no-capture">{value}</span>,
    },
    isOnboardingEnabled
      ? ({
          dataIndex: "intake",
          key: "intake",
          title: "Intake",
          render: (value: IntakeType, record: FronteraClient) => (
            <div className="min-w-36">
              <Badge
                appearance={BadgeSettingFromIntake[value]}
                text={`${value}${value === "In Progress" ? ` (${record.intakePagesCompleted} of 10)` : ""}`}
              />
            </div>
          ),
        } as Column<FronteraClient>)
      : null,
    {
      dataIndex: "created",
      key: "created",
      title: "Created",
      render: (value: Date) => (value ? format(value, "MM/dd/yyyy") : ""),
    },
    isOnboardingEnabled
      ? ({
          dataIndex: "nextAppointment",
          key: "nextAppointment",
          title: "Next Appointment",
          render: (value: Date) => (value ? format(value, "MM/dd/yyyy") : ""),
        } as Column<FronteraClient>)
      : null,
    isOnboardingEnabled
      ? ({
          dataIndex: "nearestLocation",
          key: "nearestLocation",
          title: "Nearest Clinic",
        } as Column<FronteraClient>)
      : null,
    isConsentRequired
      ? {
          dataIndex: "hasCaregiverConsent",
          key: "hasCaregiverConsent",
          title: (
            <div className="flex flex-row gap-2 items-center">
              Video Consent
              <Tooltip
                text="Video recording is enabled if at least one caregiver has signed a video consent. Consent distribution and tracking is managed by your clinic."
                placement="top"
              />
            </div>
          ),
          render: (value: boolean) => (value ? "Signed" : "Not Signed"),
        }
      : null,
  ].filter((col): col is Column<FronteraClient> => col !== null);

  const emptyImportState = onImport ? (
    <div className="flex flex-col items-center justify-center py-16 gap-2">
      <Paragraph displayType="loud" colorType="primary" size="medium">
        No clients yet
      </Paragraph>
      <Paragraph size="small" colorType="secondary" className="text-center w-[350px]">
        Looks like you don&apos;t have any clients imported yet. Import from your EHR to get started.
      </Paragraph>
      <button
        type="button"
        className="flex items-center gap-2 text-sm font-semibold text-interaction-primary cursor-pointer"
        onClick={onImport}
      >
        <ArrowDownTrayIcon className="h-6 w-6" />
        Import
      </button>
    </div>
  ) : undefined;

  return (
    <div className="mt-4 flow-root bg-white w-full">
      <Table
        onRowClick={onRowClick}
        defaultItemsPerPage={10}
        showPagination
        loading={loading}
        showSearch
        inputClassName="w-96"
        itemsPerPageOptions={[5, 10, 20, 50]}
        emptyMessage={emptyImportState || emptyMessage}
        actions={[
          isConsentRequired && {
            label: "Manage Video Consent",
            Icon: DocumentCheckIcon,
            callback: (client: FronteraClient) => setManagingConsentForClient(client),
          },
          {
            label: "View",
            Icon: EyeIcon,
            callback: (client) => {
              go(`/clients/${client.id}`, {
                state: {
                  id: client.id,
                  name: client.name,
                  intake: client.intake,
                  stage: client.stage,
                },
              });
            },
          },
        ]}
        data={(clients.filter((client) => client !== null) as FronteraClient[]).sort((clientA, clientB) =>
          clientB.created > clientA.created ? 1 : -1,
        )}
        columns={columns}
      />
      {managingConsentForClient && (
        <ManageVideoConsentFormDialog
          onClose={() => setManagingConsentForClient(undefined)}
          client={managingConsentForClient}
        />
      )}
    </div>
  );
};
